import { Button, TYPE } from '@common/Button/Button';
import { useTranslation } from 'next-i18next';
import { getLocale, LANG_DE_DE, TRANSLATION_CONFIGURATION } from '@libs/constants';
import { EXTERNAL_LINKS } from '@libs/redirect.helper';
import { Icon, IconColor, IconID } from '@common/Icon/Icon';
import { useRouter } from 'next/router';
import { getLocaleWithFallbackRegion } from '@libs/i18n.helper';
import Tippy from '@tippyjs/react';
import type { KetteEinbauArt } from '@hooks/states/echainConfigSlice';

export enum LifetimeCalculatorModes {
  CHAINFLEX = 'chainflex',
  READYCABLE = 'readycable',
  BALLBEARING = 'ballbearing',
  ECHAIN = 'echain',
  NONE = 'none',
}

export interface DecisionToolsBoxProps {
  showExpertSystem: boolean;
  showEplan: boolean;
  lifetimeCalculatorMode: LifetimeCalculatorModes;
  showZuken: boolean;
  showSampleBox: boolean;
  showCAD: boolean;
  showLifeTimeBearingMode: boolean;
  showRoleConfigurator: boolean;
  showLeadScrewConfigurator: boolean;
  variantSku: string;
  description: string;
  disabled?: boolean;
  verfahrweg?: number;
  effektiveLaenge?: number;
  numGlieder?: number;
  einbauArt?: KetteEinbauArt;
  quantity?: number;
  length?: number;
}

const wrapButtonInTooltip = (button: JSX.Element, tooltipContent: string) => (
  <Tippy content={<p>{tooltipContent}</p>} theme="light-border">
    {/* wrap button in div so tippy can recognize mouseevents */}
    <div>{button}</div>
  </Tippy>
);

export const DecisionToolsBox = ({
  showExpertSystem,
  showEplan,
  lifetimeCalculatorMode,
  showZuken,
  showSampleBox,
  showCAD,
  showLifeTimeBearingMode,
  showRoleConfigurator,
  showLeadScrewConfigurator,
  variantSku,
  description,
  disabled = false,
  verfahrweg,
  effektiveLaenge,
  numGlieder,
  einbauArt,
  quantity,
  length,
}: DecisionToolsBoxProps) => {
  const router = useRouter();

  const { t, i18n } = useTranslation('product/decisionToolsBox');

  const locale = getLocale(router);
  const localeWithFallbackRegion = getLocaleWithFallbackRegion(locale);

  let lifetimeCalcButton: JSX.Element | undefined;

  switch (lifetimeCalculatorMode) {
    case LifetimeCalculatorModes.ECHAIN:
      lifetimeCalcButton = (
        <Button
          href={EXTERNAL_LINKS.ECHAIN_LIFETIME_CALC(variantSku, verfahrweg, einbauArt, locale)}
          external
          useWindowNavigationHref
          text={t('DECISION_TOOLS_BOX.CALCULATE_LIFETIME')}
          icon={<Icon size={10} color={IconColor.WHITE} id={IconID.LIFETIME_CALCULATOR} />}
          disabled={disabled}
          type={TYPE.SECONDARY}
        />
      );
      break;
    case LifetimeCalculatorModes.READYCABLE:
      lifetimeCalcButton = (
        <Button
          href={EXTERNAL_LINKS.READYCABLE_PRODUCT_FINDER(variantSku, locale)}
          external
          useWindowNavigationHref
          text={t('DECISION_TOOLS_BOX.CALCULATE_LIFETIME')}
          icon={<Icon size={10} color={IconColor.WHITE} id={IconID.LIFETIME_CALCULATOR} />}
          disabled={disabled}
          type={TYPE.SECONDARY}
        />
      );
      break;
    case LifetimeCalculatorModes.CHAINFLEX:
      lifetimeCalcButton = (
        <Button
          href={EXTERNAL_LINKS.CHAINFLEX_LIFETIME_CALC(variantSku, locale)}
          external
          text={t('DECISION_TOOLS_BOX.CALCULATE_LIFETIME')}
          useWindowNavigationHref
          icon={<Icon size={10} color={IconColor.WHITE} id={IconID.LIFETIME_CALCULATOR} />}
          disabled={disabled}
          type={TYPE.SECONDARY}
        />
      );
      break;
    default:
      lifetimeCalcButton = undefined;
  }

  const lifetimeBearingButton = (
    <Button
      href={EXTERNAL_LINKS.BALL_BEARING_CALC(variantSku, locale)}
      useWindowNavigationHref
      text={t('DECISION_TOOLS_BOX.CALCULATE_LIFETIME')}
      icon={<Icon size={10} color={IconColor.WHITE} id={IconID.DOWNLOAD_PLAN} />}
      disabled={disabled}
    />
  );

  const roleConfiguratorButton = (
    <Button
      href={EXTERNAL_LINKS.BALL_BEARING_CONFIG(variantSku, locale)}
      external
      useWindowNavigationHref
      text={t('DECISION_TOOLS_BOX.ROLE_CONFIGURATOR')}
      icon={<Icon size={10} color={IconColor.WHITE} id={IconID.DOWNLOAD_PLAN} />}
      disabled={disabled}
    />
  );

  const eplanButton = (
    <Button
      href={EXTERNAL_LINKS.EPLAN(variantSku)}
      external
      useWindowNavigationHref
      text={t('DECISION_TOOLS_BOX.EPLAN')}
      icon={<Icon size={10} color={IconColor.WHITE} id={IconID.DOWNLOAD_PLAN} />}
      disabled={disabled}
    />
  );
  const expertSystemButton = (
    <Button
      type={TYPE.SECONDARY}
      href={EXTERNAL_LINKS.EXPERT_TOOL(localeWithFallbackRegion ?? TRANSLATION_CONFIGURATION.defaultLocale)}
      external
      useWindowNavigationHref
      text={t('DECISION_TOOLS_BOX.EXPERT_SYSTEM')}
      icon={<Icon size={10} color={IconColor.WHITE} id={IconID.LIFETIME_CALCULATOR} />}
      disabled={disabled}
    />
  );

  const sampleBoxButton = (
    <Button
      href={EXTERNAL_LINKS.SAMPLE(variantSku, description, locale)}
      useWindowNavigationHref
      type={TYPE.PRIMARY}
      text={t('DECISION_TOOLS_BOX.REQUIRE_TEMPLATE')}
      icon={<Icon size={10} color={IconColor.WHITE} id={IconID.FREE_SAMPLE} />}
      disabled={disabled}
    />
  );

  const dryspinConfiguratorButton = (
    <Button
      href={EXTERNAL_LINKS.DRYSPIN_CONFIGURATOR(variantSku, quantity, length, locale)}
      external
      useWindowNavigationHref
      text={t('DECISION_TOOLS_BOX.LEADSCREW_CONFIGURATOR')}
      icon={<Icon size={10} color={IconColor.WHITE} id={IconID.DOWNLOAD_PLAN} />}
      disabled={disabled}
    />
  );

  return lifetimeCalculatorMode !== LifetimeCalculatorModes.NONE ||
    showExpertSystem ||
    showEplan ||
    showSampleBox ||
    showZuken ||
    showCAD ||
    showLifeTimeBearingMode ||
    showRoleConfigurator ||
    showLeadScrewConfigurator ? (
    <div className="bg-gray-50 p-8 mt-8 w-full" data-testid="decision-toolbox">
      <div className="text-3xl mb-4">{t('DECISION_TOOLS_BOX.LABEL')}</div>
      {showExpertSystem &&
        (disabled
          ? wrapButtonInTooltip(expertSystemButton, t('DECISION_TOOLS_BOX.DISABLED.EXPERT_SYSTEM'))
          : expertSystemButton)}
      {lifetimeCalculatorMode !== LifetimeCalculatorModes.NONE &&
        lifetimeCalcButton &&
        (disabled
          ? wrapButtonInTooltip(lifetimeCalcButton, t('DECISION_TOOLS_BOX.DISABLED.CALCULATE_LIFETIME'))
          : lifetimeCalcButton)}
      {showLifeTimeBearingMode &&
        (disabled
          ? wrapButtonInTooltip(lifetimeBearingButton, t('DECISION_TOOLS_BOX.DISABLED.EPLAN'))
          : lifetimeBearingButton)}
      {showRoleConfigurator &&
        (disabled
          ? wrapButtonInTooltip(roleConfiguratorButton, t('DECISION_TOOLS_BOX.DISABLED.EPLAN'))
          : roleConfiguratorButton)}
      {showLeadScrewConfigurator && dryspinConfiguratorButton}
      {showEplan && (disabled ? wrapButtonInTooltip(eplanButton, t('DECISION_TOOLS_BOX.DISABLED.EPLAN')) : eplanButton)}
      {showSampleBox &&
        (disabled
          ? wrapButtonInTooltip(sampleBoxButton, t('DECISION_TOOLS_BOX.DISABLED.REQUIRE_TEMPLATE'))
          : sampleBoxButton)}
      {showZuken && i18n.language === LANG_DE_DE && (
        <Button
          href={EXTERNAL_LINKS.ZUKEN(variantSku)}
          external
          useWindowNavigationHref
          text={t('DECISION_TOOLS_BOX.ZUKEN')}
          icon={<Icon size={10} color={IconColor.WHITE} id={IconID.ZUKEN} useSprite={false} />}
        />
      )}
      {showCAD && (
        <Button
          href={EXTERNAL_LINKS.CAD(variantSku, locale, verfahrweg, effektiveLaenge, numGlieder, einbauArt)}
          external
          useWindowNavigationHref
          testId="download-cad-button"
          text={t('DECISION_TOOLS_BOX.CAD')}
          icon={<Icon size={10} color={IconColor.WHITE} id={IconID.CAD_FILES} />}
        />
      )}
    </div>
  ) : null;
};
